/**
 * API services for files, groups, and subscription
 * Uses apiWithRetry for 401 retry with token refresh
 */

import { apiWithRetry } from '../lib/api-interceptor';
import { useAuthStore } from '../stores/authStore';

export interface ContextGroup {
  id: string;
  name: string;
  icon: string;
  color: string;
}

export interface FileItem {
  id: string;
  name: string;
  mimeType?: string | null;
  fileType?: 'document' | 'image' | 'audio';
  fileSize?: number | null;
  createdAt: string;
  thumbnailUrl?: string | null;
  thumbnailPath?: string | null;
  storagePath?: string;
}

export interface SubscriptionStatus {
  isActive: boolean;
  plan: string;
  source?: string | null;
  expiresAt?: string | null;
}

const getAuthParams = () => {
  const anonymousId = useAuthStore.getState().anonymousId;
  const params: Record<string, string> = {};
  if (anonymousId) params.anonymousId = anonymousId;
  return params;
};

export const listContextGroups = async (): Promise<{ success: boolean; data?: ContextGroup[] }> => {
  const res = await apiWithRetry<{ success: boolean; data?: ContextGroup[] }>({
    url: '/list-context-groups',
    method: 'GET',
    params: getAuthParams(),
  });
  return res;
};

export const createContextGroup = async (data: {
  name: string;
  icon?: string;
  color?: string;
}): Promise<{ success: boolean; data?: ContextGroup }> => {
  return apiWithRetry({
    url: '/create-context-group',
    method: 'POST',
    data,
  });
};

export const updateContextGroup = async (
  id: string,
  data: { name?: string; icon?: string; color?: string }
) => {
  return apiWithRetry({
    url: `/update-context-group/${id}`,
    method: 'PATCH',
    data,
  });
};

export const deleteContextGroup = async (id: string) => {
  return apiWithRetry({
    url: `/delete-context-group/${id}`,
    method: 'DELETE',
  });
};

export const listFiles = async (params?: {
  period?: string;
  timezoneOffset?: string;
}): Promise<{ success: boolean; data?: FileItem[] }> => {
  const q: Record<string, string> = { ...getAuthParams() };
  if (params?.period) q.period = params.period;
  if (params?.timezoneOffset) q.timezoneOffset = params.timezoneOffset;
  return apiWithRetry({
    url: '/list-files',
    method: 'GET',
    params: Object.keys(q).length ? q : undefined,
  });
};

export const searchFiles = async (
  q: string,
  params?: { period?: string; timezoneOffset?: string }
): Promise<{ success: boolean; data?: FileItem[] }> => {
  const all: Record<string, string> = { q, ...getAuthParams() };
  if (params?.period) all.period = params.period;
  if (params?.timezoneOffset) all.timezoneOffset = params.timezoneOffset;
  return apiWithRetry({
    url: '/search-files',
    method: 'GET',
    params: all,
  });
};

export const uploadFile = async (file: File): Promise<{ success: boolean; data?: FileItem }> => {
  const formData = new FormData();
  formData.append('file', file);
  return apiWithRetry({
    url: '/upload-file',
    method: 'POST',
    data: formData,
  });
};

export interface FileWithDownloadUrl extends FileItem {
  downloadUrl?: string;
  downloadUrlExpiresAt?: string;
}

export const getFile = async (id: string): Promise<{ success: boolean; data?: FileWithDownloadUrl }> => {
  return apiWithRetry({
    url: `/get-file/${id}`,
    method: 'GET',
  });
};

export const updateFile = async (
  id: string,
  data: { name?: string }
): Promise<{ success: boolean; data?: FileItem }> => {
  return apiWithRetry({
    url: `/update-file/${id}`,
    method: 'PATCH',
    data,
  });
};

export const deleteFile = async (id: string) => {
  return apiWithRetry({
    url: `/delete-file/${id}`,
    method: 'DELETE',
  });
};

export const checkSubscription = async (): Promise<SubscriptionStatus> => {
  const res = await apiWithRetry<{ success: boolean; data?: SubscriptionStatus }>({
    url: '/check-subscription',
    method: 'GET',
  });
  if (res?.data) return res.data;
  return { isActive: false, plan: 'free' };
};

export type PriceId = 'pro_monthly' | 'pro_yearly' | 'lifetime';

export const createCheckoutSession = async (priceId: PriceId): Promise<{ url: string }> => {
  const res = await apiWithRetry<{ success: boolean; data?: { url: string } }>({
    url: '/create-checkout-session',
    method: 'POST',
    data: { priceId },
  });
  if (!res?.data?.url) {
    throw new Error('Failed to create checkout session');
  }
  return { url: res.data.url };
};
